import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Logo } from "./Logo";
import LeadDialog from "./LeadDialog";
import { NAV, CONTACT } from "../lib/site";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-isav-bg/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-home-logo" aria-label="Integrated Solutions AV home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV.slice(1).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `relative font-sans text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? "text-white" : "text-white/55 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-call-now"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55 transition-colors hover:text-white"
          >
            <Phone className="h-3.5 w-3.5" /> Call Now
          </a>
          <LeadDialog kind="consultation" variant="primary" triggerClassName="!px-6 !py-3" testId="nav-book-consultation" />
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-isav-bg/95 border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-3 font-serif text-2xl ${isActive ? "text-white" : "text-white/60"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-4" onClick={() => setOpen(false)}>
                <LeadDialog kind="consultation" variant="accent" triggerClassName="w-full" testId="mobile-book-consultation" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
